import React, { useEffect, useState } from 'react';
import styles from './enrichment-snapshot.module.sass';
import cx from 'classnames';
import { Col, Row, Typography, Space, Tooltip, Divider, Spin } from 'antd';
import { Link } from 'react-router-dom';
import {
  LineChartOutlined,
  RetweetOutlined,
  CrownOutlined,
  RadarChartOutlined,
  EyeOutlined,
  FolderOpenFilled,
  LoadingOutlined,
} from '@ant-design/icons';
import ExpiredCertificateIcon from '../../../../assets/certificate_expire.svg';
import ParkedIcon from '../../../../assets/threat-ranking/threat-detail/parked.svg';
import SinkholeIcon from '../../../../assets/threat-ranking/threat-detail/sinkhole.svg';
import IPFSIcon from '../../../../assets/threat-ranking/threat-detail/ipfs.svg';
import DynamicIcon from '../../../../assets/threat-ranking/threat-detail/is_dsl_dynamic.svg';
import TorIcon from '../../../../assets/threat-ranking/threat-detail/tor_exit_node.svg';
import UrlIcon from '../../../../assets/threat-ranking/threat-detail/url_shortener.svg';
import PartOfSourceIcon from '../../../../assets/threat-ranking/threat-detail/part_of_source.svg';
import LoadingSkeleton from '../../../../shared-components/LoadingSkeleton';
import ProgressCircleSmall from '../../../../shared-components/ProgressCircleSmall';
import S3BucketIcon from '../../../../assets/s3.svg';
import { STATUS } from '../../../../redux/constants';

function EnrichmentSnapshot({
  oldData,
  statusOld,
  newData,
  statusNew,
  fallbackData,
  fallbackStatus,
  whoisData,
  whoisStatus,
  type,
  props,
  openAllCollapse,
}) {
  const { Title, Text } = Typography;
  let newEnrichmentData = undefined;

  if (type === 'domain') {
    newEnrichmentData = newData?.response;
  } else if (type === 'ipv4') {
    if (newData?.response?.ip2asn) {
      newEnrichmentData = newData?.response?.ip2asn?.[0];
    }
  }

  const [whoIsData, setWhoIsData] = useState({});
  const [whoIsFallbackData, setWhoIsFallbackData] = useState({});

  useEffect(() => {
    if (whoisStatus === STATUS.LOADED && whoisData?.response) {
      setWhoIsData(whoisData?.response?.whois_live);
    }
  }, [whoisStatus]);

  useEffect(() => {
    if (fallbackStatus == STATUS.LOADED && fallbackData?.response) {
      setWhoIsFallbackData(fallbackData?.response?.whois);
    }
  }, [fallbackStatus]);

  const handleHighlightTable = (id) => {
    if (id === null) {
      return;
    }

    openAllCollapse();

    setTimeout(() => {
      document
        .getElementById(id)
        .scrollIntoView({ behavior: 'smooth', block: 'center' });
      document.getElementById(id).classList.add('highlightEnrichmentTable');
    }, 300);

    setTimeout(() => {
      document.getElementById(id).classList.remove('highlightEnrichmentTable');
    }, 3000);
  };

  const returnAgeValue = (domainAge, whoisAge) => {
    if ((domainAge || domainAge === 0) && (whoisAge || whoisAge === 0)) {
      if (domainAge > whoisAge || domainAge === whoisAge) {
        return domainAge;
      } else {
        return whoisAge;
      }
    }

    if (domainAge || domainAge === 0) {
      return domainAge;
    }
    if (whoisAge || whoisAge === 0) {
      return whoisAge;
    }

    return '-';
  };
  const returnAgeHighlightType = (domainAge, whoisAge) => {
    if ((domainAge || domainAge === 0) && (whoisAge || whoisAge === 0)) {
      if (domainAge > whoisAge || domainAge === whoisAge) {
        return 'enrichmentDomainInformation';
      } else {
        return 'enrichmentWhoisInformation';
      }
    }

    if (domainAge || domainAge === 0) {
      return 'enrichmentDomainInformation';
    }
    if (whoisAge || whoisAge === 0) {
      return 'enrichmentWhoisInformation';
    }

    return null;
  };

  const statusLoaded = () => {
    if (type === 'domain') {
      return statusNew === STATUS.LOADED && fallbackStatus === STATUS.LOADED;
    } else if (type === 'ipv4') {
      return statusNew === STATUS.LOADED;
    }
  };

  if (statusLoaded()) {
    return (
      <div className={styles.enrichmentSnapshot}>
        <Row className={styles.flags}>
          <Col xs={24}>
            <Title level={4}>Flag(s)</Title>
            <div id="flagsContainer" className={styles.flags_container}>
              <span className={styles.flagsNoneStyle}>
                {newEnrichmentData?.domain_urls?.results_summary
                  ?.alexa_top10k ||
                newEnrichmentData?.domain_urls?.results_summary
                  ?.is_dynamic_domain ||
                newEnrichmentData?.domain_string_frequency_probability
                  ?.dga_probability_score == 100 ||
                newEnrichmentData?.domain_urls?.results_summary
                  ?.is_url_shortener ||
                newEnrichmentData?.ns_reputation?.is_parked ||
                newEnrichmentData?.ns_reputation?.is_sinkholed ||
                newEnrichmentData?.domaininfo?.is_new_score == 100 ||
                newEnrichmentData?.benign_info?.known_benign ||
                newEnrichmentData?.sinkhole_info?.known_sinkhole_ip ||
                newEnrichmentData?.ip_is_dsl_dynamic ||
                newEnrichmentData?.ip_has_expired_certificate ||
                newEnrichmentData?.host_flags?.host_has_expired_certificate ||
                newEnrichmentData?.ip_has_open_directory ||
                newEnrichmentData?.host_flags?.host_has_open_directory ||
                newEnrichmentData?.ip_is_tor_exit_node ||
                newEnrichmentData?.ip_is_ipfs_node ||
                oldData?.feeds_containing_ioc?.length > 0
                  ? ''
                  : '-'}
              </span>

              {newEnrichmentData?.domain_urls?.results_summary
                ?.alexa_top10k && (
                <Tooltip
                  className={styles.flag}
                  placement="bottom"
                  title="The domain is in the top 10,000 domains, by Alexa ranking."
                >
                  <LineChartOutlined
                    className={cx(styles.icon, styles.alexa)}
                  />
                  <span className={styles.text}>Alexa Top 10k</span>
                </Tooltip>
              )}

              {newEnrichmentData?.domain_urls?.results_summary
                ?.is_dynamic_domain && (
                <Tooltip
                  className={styles.flag}
                  placement="bottom"
                  title="Establishes if an IP address belongs to dynamic infrastructure - i.e. may be dynamically assigned by ISPs or for residential use."
                >
                  <RetweetOutlined className={cx(styles.icon, styles.dsl)} />
                  <span className={styles.text}>DSL Dynamic</span>
                </Tooltip>
              )}

              {newEnrichmentData?.domain_string_frequency_probability
                ?.dga_probability_score == 100 && (
                <Tooltip
                  className={styles.flag}
                  placement="bottom"
                  title="Is part of generation algorithm."
                >
                  <RadarChartOutlined className={cx(styles.icon, styles.dga)} />
                  <span className={styles.text}>Part of DGA</span>
                </Tooltip>
              )}

              {newEnrichmentData?.domain_urls?.results_summary
                ?.is_url_shortener && (
                <Tooltip
                  className={styles.flag}
                  placement="bottom"
                  title="A URL shortening service that points to a longer domain."
                >
                  <img
                    className={styles.icon}
                    src={UrlIcon}
                    alt="url shortened icon"
                    title="url shortened"
                  />
                  <span className={styles.text}>URL Shortener</span>
                </Tooltip>
              )}

              {newEnrichmentData?.ns_reputation?.is_parked && (
                <Tooltip
                  className={styles.flag}
                  placement="bottom"
                  title="The domain is registered, but not connected to any online service (e.g., a website or email server)."
                >
                  <img
                    className={styles.icon}
                    src={ParkedIcon}
                    alt="parked icon"
                    title="parked"
                  />
                  <span className={styles.text}>Parked</span>
                </Tooltip>
              )}

              {newEnrichmentData?.ns_reputation?.is_sinkholed && (
                <Tooltip
                  className={styles.flag}
                  placement="bottom"
                  title="The domain points to an IP sinkhole, preventing DNS resolution."
                >
                  <img
                    className={styles.icon}
                    src={SinkholeIcon}
                    alt="sinkhole icon"
                    title="sinkhole"
                  />
                  <span className={styles.text}>Sinkholed</span>
                </Tooltip>
              )}

              {newEnrichmentData?.domaininfo?.is_new_score == 100 && (
                <Tooltip
                  className={styles.flag}
                  placement="bottom"
                  title="A domain that hasn’t previously been seen in any zone files."
                >
                  <CrownOutlined className={cx(styles.icon, styles.new)} />
                  <span className={styles.text}>New</span>
                </Tooltip>
              )}

              {newEnrichmentData?.benign_info?.known_benign && (
                <Tooltip
                  className={styles.flag}
                  placement="bottom"
                  title="The IP has been seen to belong to a benign internet scanning service, or similar non-malicious operator."
                >
                  <EyeOutlined className={cx(styles.icon, styles.eye)} />
                  <span className={styles.text}>Known Benign</span>
                </Tooltip>
              )}

              {newEnrichmentData?.sinkhole_info?.known_sinkhole_ip && (
                <Tooltip
                  className={styles.flag}
                  placement="bottom"
                  title="An IP address that prevents DNS servers from resolving a particular domain."
                >
                  <img
                    className={styles.icon}
                    src={SinkholeIcon}
                    alt="sinkhole icon"
                    title="sinkhole"
                  />
                  <span className={styles.text}>Sinkhole (IP)</span>
                </Tooltip>
              )}

              {newEnrichmentData?.ip_is_dsl_dynamic && (
                <Tooltip
                  className={styles.flag}
                  placement="bottom"
                  title="Establishes if an IP address belongs to dynamic infrastructure - i.e. may be dynamically assigned by ISPs or for residential use."
                >
                  <img
                    className={styles.icon}
                    src={DynamicIcon}
                    alt="dynamic icon"
                    title="dynamic"
                  />
                  <span className={styles.text}>Dynamic IP Data</span>
                </Tooltip>
              )}

              {(newEnrichmentData?.ip_has_expired_certificate ||
                newEnrichmentData?.host_flags?.[0]
                  ?.host_has_expired_certificate) &&
                (props?.mySubscriptionPlan?.has_access_to_query_builder &&
                statusOld === STATUS.LOADED ? (
                  <>
                    <Tooltip
                      className={cx(styles.flag, styles.link)}
                      placement="bottom"
                      title="The IP address has an expired certificate."
                    >
                      <Link
                        to={
                          `/explore/query-builder?query=cf2c717a-ebd4-44be-87ab-5028a52dd78d&ipv4=${oldData?.ioc_name}&backLink=` +
                          encodeURIComponent(window.location.pathname) +
                          encodeURIComponent(window.location.search)
                        }
                      >
                        <img
                          className={cx(styles.icon, styles.cancel_border)}
                          src={ExpiredCertificateIcon}
                          alt="expired icon"
                          title="expired"
                        />
                        <span className={styles.text}>Expired Certificate</span>
                      </Link>
                    </Tooltip>
                  </>
                ) : (
                  <Tooltip
                    title={
                      props?.mySubscriptionPlan?.has_access_to_explore ? (
                        ''
                      ) : (
                        <>
                          This option is not part of this subscription plan. To
                          upgrade your subscription contact us at{' '}
                          <a target="_blank" href="mailto:sales@silentpush.com">
                            sales@silentpush.com
                          </a>
                          .
                        </>
                      )
                    }
                  >
                    <img
                      className={cx(styles.icon, styles.cancel_border)}
                      src={ExpiredCertificateIcon}
                      alt="expired icon"
                      title="expired"
                    />
                    <span className={styles.text}>Expired Certificate</span>
                  </Tooltip>
                ))}

              {(newEnrichmentData?.ip_has_open_directory ||
                newEnrichmentData?.host_flags?.[0]?.host_has_open_directory) &&
                (props?.mySubscriptionPlan?.has_access_to_query_builder &&
                statusOld === STATUS.LOADED ? (
                  <>
                    <Tooltip
                      className={cx(styles.flag, styles.link, {
                        [styles.disabled]: type === 'domain',
                      })}
                      placement="bottom"
                      title={`The ${type} address has an open directory served by a web server.`}
                    >
                      <Link
                        to={
                          `/explore/query-builder?query=65d60b09-5f75-4153-b444-802eed7b91ad&ipv4=${oldData?.ioc_name}&backLink=` +
                          encodeURIComponent(window.location.pathname) +
                          encodeURIComponent(window.location.search)
                        }
                      >
                        <FolderOpenFilled
                          className={cx(styles.icon, styles.directory)}
                        />
                        <span className={styles.text}>
                          Has Open Directories
                        </span>
                      </Link>
                    </Tooltip>
                  </>
                ) : (
                  <Tooltip
                    title={
                      props?.mySubscriptionPlan?.has_access_to_explore ? (
                        ''
                      ) : (
                        <>
                          This option is not part of this subscription plan. To
                          upgrade your subscription contact us at{' '}
                          <a target="_blank" href="mailto:sales@silentpush.com">
                            sales@silentpush.com
                          </a>
                          .
                        </>
                      )
                    }
                  >
                    <FolderOpenFilled
                      className={cx(styles.icon, styles.directory)}
                    />
                    <span className={styles.text}>Has Open Directories</span>
                  </Tooltip>
                ))}

              {newEnrichmentData?.host_flags?.[0]?.host_has_open_s3_bucket && (
                <>
                  <Tooltip
                    className={cx(styles.flag, styles.link, {
                      [styles.disabled]: type === 'domain',
                    })}
                    placement="bottom"
                    title={`The ${type} address has an open S3 bucket.`}
                  >
                    {/* <Link
                        to={
                          `/explore/query-builder?query=65d60b09-5f75-4153-b444-802eed7b91ad&ipv4=${oldData?.ioc_name}&backLink=` +
                          encodeURIComponent(window.location.pathname) +
                          encodeURIComponent(window.location.search)
                        }
                      > */}
                    <img
                      className={cx(styles.icon, styles.cancel_border)}
                      src={S3BucketIcon}
                      alt="s3 bucket icon"
                      title="s3"
                    />
                    <span className={styles.text}>Has Open S3 Bucket</span>
                    {/* </Link> */}
                  </Tooltip>
                </>
              )}

              {newEnrichmentData?.ip_is_tor_exit_node && (
                <Tooltip
                  className={styles.flag}
                  placement="bottom"
                  title="An exit gateway connecting tor traffic to the internet."
                >
                  <img
                    className={styles.icon}
                    src={TorIcon}
                    alt="tor icon"
                    title="tor"
                  />
                  <span className={styles.text}>Tor Exit Node</span>
                </Tooltip>
              )}

              {newEnrichmentData?.ip_is_ipfs_node && (
                <Tooltip
                  className={styles.flag}
                  placement="bottom"
                  title="The IP address is an IPFS node."
                >
                  <img
                    className={styles.icon}
                    src={IPFSIcon}
                    alt="ipfs icon"
                    title="ipfs"
                  />
                  <span className={styles.text}>IPFS Node</span>
                </Tooltip>
              )}

              {oldData?.feeds_containing_ioc?.length > 0 &&
                statusOld === STATUS.LOADED && (
                  <Tooltip
                    className={styles.flag}
                    placement="bottom"
                    title="This observable is present in at least one Live Threat Feed, click to scroll to section."
                  >
                    <a href="#enrichment-sources" className={styles.naked_link}>
                      <img
                        className={styles.icon}
                        src={PartOfSourceIcon}
                        alt="part of source icon"
                        title="part-of-source"
                      />
                      <span className={styles.text}>
                        Part of Live Threat Feeds
                      </span>
                    </a>
                  </Tooltip>
                )}
            </div>
          </Col>
        </Row>

        <Row className={cx(styles.highlights, 'mt-1')}>
          <Title level={4}>Highlights</Title>

          <Col xs={24} className={styles.highlightsColumn}>
            <Space size={'middle'} className={styles.highlightedProgress}>
              {type === 'domain' && (
                <>
                  <a
                    onClick={() =>
                      handleHighlightTable('enrichmentNameServerInformation')
                    }
                  >
                    <ProgressCircleSmall
                      className={styles.domainProgress}
                      percent={
                        newEnrichmentData?.ns_reputation?.ns_reputation_score
                          ? newEnrichmentData?.ns_reputation
                              ?.ns_reputation_score
                          : 0
                      }
                      amount={
                        newEnrichmentData?.ns_reputation?.ns_reputation_score
                          ? newEnrichmentData?.ns_reputation
                              ?.ns_reputation_score
                          : 0
                      }
                      width={70}
                      serial={1}
                      title={'NS Reputation'}
                    />
                  </a>
                  <a
                    onClick={() =>
                      handleHighlightTable('enrichmentNameServerChanges')
                    }
                  >
                    <ProgressCircleSmall
                      className={styles.domainProgress}
                      percent={
                        newEnrichmentData?.nschanges?.results_summary
                          ?.ns_entropy_score
                          ? newEnrichmentData?.nschanges?.results_summary
                              ?.ns_entropy_score
                          : 0
                      }
                      amount={
                        newEnrichmentData?.nschanges?.results_summary
                          ?.ns_entropy_score
                          ? newEnrichmentData?.nschanges?.results_summary
                              ?.ns_entropy_score
                          : 0
                      }
                      width={70}
                      serial={2}
                      title={'NS Entropy'}
                    />
                  </a>
                  <a
                    onClick={() =>
                      handleHighlightTable('enrichmentCuratedFeedHistory')
                    }
                  >
                    <ProgressCircleSmall
                      className={styles.domainProgress}
                      percent={
                        newEnrichmentData?.listing_score
                          ? newEnrichmentData?.listing_score
                          : 0
                      }
                      amount={
                        newEnrichmentData?.listing_score
                          ? newEnrichmentData?.listing_score
                          : 0
                      }
                      width={70}
                      serial={3}
                      title={'Curated Feeds History Score'}
                    />
                  </a>
                </>
              )}

              {type === 'ipv4' && (
                <>
                  <a
                    onClick={() =>
                      handleHighlightTable('enrichmentIpReputation')
                    }
                  >
                    <ProgressCircleSmall
                      className={styles.ipProgress}
                      percent={
                        newEnrichmentData?.ip_reputation
                          ? newEnrichmentData?.ip_reputation
                          : 0
                      }
                      amount={
                        newEnrichmentData?.ip_reputation
                          ? newEnrichmentData?.ip_reputation
                          : 0
                      }
                      width={70}
                      serial={1}
                      title={'IP Reputation'}
                    />
                  </a>
                  <a
                    onClick={() =>
                      handleHighlightTable('enrichmentAsnInformation')
                    }
                  >
                    <ProgressCircleSmall
                      className={styles.ipProgress}
                      percent={
                        newEnrichmentData?.asn_reputation
                          ? newEnrichmentData?.asn_reputation
                          : 0
                      }
                      amount={
                        newEnrichmentData?.asn_reputation
                          ? newEnrichmentData?.asn_reputation
                          : 0
                      }
                      width={70}
                      serial={2}
                      title={'ASN Reputation'}
                    />
                  </a>
                  <a
                    onClick={() =>
                      handleHighlightTable('enrichmentAsnInformation')
                    }
                  >
                    <ProgressCircleSmall
                      className={styles.ipProgress}
                      percent={
                        newEnrichmentData?.asn_takedown_reputation
                          ? newEnrichmentData?.asn_takedown_reputation
                          : 0
                      }
                      amount={
                        newEnrichmentData?.asn_takedown_reputation
                          ? newEnrichmentData?.asn_takedown_reputation
                          : 0
                      }
                      width={70}
                      serial={3}
                      title={'ASN Takedown Reputation'}
                    />
                  </a>
                  <a
                    onClick={() =>
                      handleHighlightTable('enrichmentAsnInformation')
                    }
                  >
                    <ProgressCircleSmall
                      className={styles.ipProgress}
                      percent={
                        newEnrichmentData?.asn_rank
                          ? newEnrichmentData?.asn_rank
                          : 0
                      }
                      amount={
                        newEnrichmentData?.asn_rank
                          ? newEnrichmentData?.asn_rank
                          : 0
                      }
                      width={70}
                      serial={4}
                      title={'ASN Rank'}
                    />
                  </a>
                  <a
                    onClick={() =>
                      handleHighlightTable('enrichmentSubnetInformation')
                    }
                  >
                    <ProgressCircleSmall
                      className={styles.ipProgress}
                      percent={
                        newEnrichmentData?.subnet_reputation
                          ? newEnrichmentData?.subnet_reputation
                          : 0
                      }
                      amount={
                        newEnrichmentData?.subnet_reputation
                          ? newEnrichmentData?.subnet_reputation
                          : 0
                      }
                      width={70}
                      serial={5}
                      title={'Subnet Reputation'}
                    />
                  </a>

                  <a
                    onClick={() =>
                      handleHighlightTable('enrichmentCuratedFeedHistory')
                    }
                  >
                    <ProgressCircleSmall
                      className={styles.ipProgress}
                      percent={
                        newEnrichmentData?.listing_score
                          ? newEnrichmentData?.listing_score
                          : 0
                      }
                      amount={
                        newEnrichmentData?.listing_score
                          ? newEnrichmentData?.listing_score
                          : 0
                      }
                      width={70}
                      serial={6}
                      title={'Curated Feeds History Score'}
                    />
                  </a>
                </>
              )}
            </Space>

            <Divider type="vertical" />

            <Space className={styles.highlightedData}>
              {type === 'domain' && (
                <>
                  <div className={styles.highlightedDataContainer}>
                    <a
                      className={styles.data}
                      onClick={() =>
                        handleHighlightTable('enrichmentIpDiversity')
                      }
                    >
                      <Text>ASN Diversity: </Text>
                      <Text strong>
                        {newEnrichmentData?.ip_diversity?.asn_diversity
                          ? newEnrichmentData?.ip_diversity?.asn_diversity
                          : '-'}
                      </Text>
                    </a>
                    <a
                      className={styles.data}
                      onClick={() =>
                        handleHighlightTable('enrichmentIpDiversity')
                      }
                    >
                      <Text>IP Diversity: </Text>
                      <Text strong>
                        {newEnrichmentData?.ip_diversity?.ip_diversity_all
                          ? newEnrichmentData?.ip_diversity?.ip_diversity_all
                          : '-'}
                      </Text>
                    </a>
                  </div>
                  <div className={styles.highlightedDataContainer}>
                    <a
                      className={styles.data}
                      onClick={() =>
                        handleHighlightTable(
                          returnAgeHighlightType(
                            newEnrichmentData?.domaininfo?.age,
                            newEnrichmentData?.domaininfo?.whois_age,
                          ),
                        )
                      }
                    >
                      <Text>Age: </Text>
                      <Text strong>
                        {returnAgeValue(
                          newEnrichmentData?.domaininfo?.age,
                          newEnrichmentData?.domaininfo?.whois_age,
                        )}
                      </Text>
                    </a>
                    <a
                      className={styles.data}
                      onClick={() =>
                        handleHighlightTable('enrichmentWhoisInformation')
                      }
                    >
                      <Text>Registrar: </Text>

                      {whoisStatus === STATUS.LOADED ? (
                        <Text strong>
                          {whoIsData?.registrar
                            ? whoIsData?.registrar
                            : whoIsFallbackData?.[0]?.registrar
                            ? whoIsFallbackData?.[0]?.registrar
                            : '-'}
                        </Text>
                      ) : (
                        <Spin indicator={<LoadingOutlined spin />} />
                      )}
                    </a>
                  </div>
                </>
              )}
              {type === 'ipv4' && (
                <>
                  <div className={styles.highlightedDataContainer}>
                    <a
                      className={styles.data}
                      onClick={() =>
                        handleHighlightTable('enrichmentIpInformation')
                      }
                    >
                      <Text>IP Density: </Text>
                      <Text strong>
                        {newEnrichmentData?.density ||
                        newEnrichmentData?.density === 0
                          ? newEnrichmentData?.density
                          : '-'}
                      </Text>
                    </a>
                  </div>
                </>
              )}
            </Space>
          </Col>
        </Row>
      </div>
    );
  } else {
    return (
      <div className={styles.enrichmentSnapshot}>
        <Row gutter={16} wrap={false} className="mb-1">
          <Col xs={24}>
            <LoadingSkeleton />
          </Col>
        </Row>
        <Row gutter={16} wrap={false}>
          <Col xs={24} md={6}>
            <LoadingSkeleton />
          </Col>
          <Col xs={24} md={18}>
            <LoadingSkeleton />
          </Col>
        </Row>
      </div>
    );
  }
}

export default EnrichmentSnapshot;
